import React, { FC, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button, ConfigProvider, Empty } from 'antd';
import { ArrowLeftOutlined, FullscreenOutlined } from '@ant-design/icons';
import LocationsMapboxGallery, {
	LOCATIONS_MAP_COUNTRY_LEVEL_MAX_ZOOM,
	hasMapCoordinate,
} from '../locations_mapbox_gallery/LocationsMapboxGallery';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import { getResolvedImageUrisFromStrings } from '../../utils/FileAccessUtils';
import { CURRENT_ENV_ROLE_TYPE } from '../../utils/EnvConfigUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { LocationContactGroup } from '../../utils/LocationsMapPopupUtils';
import { LocationExternalLinkIcon } from './LocationExternalLinkIcon';

// Buyer build → purple, supplier build → teal; mirrors the map pin tint (see MapGalleryPin).
const ROLE_LINK_COLOR_CLASS =
	CURRENT_ENV_ROLE_TYPE === ROLE_TYPES.SUPPLIER ? 'text-supplier' : 'text-buyer';

// Inline map: 570px tall, full width of the TableHoc content column. OverviewShell
// Content (0.5em) + TableHoc px-2 supply the ~16px horizontal gutter — no max-width
// cap so the map aligns with the toolbar and does not leave side gutters on wide screens.
const INLINE_MAP_CONTAINER_CLASSNAME =
	'box-border relative h-[570px] w-full overflow-hidden bg-white';

const INLINE_MAP_GALLERY_PROPS = {
	hideSearchWithinArea: true,
	baseLayerStyle: 'light',
	showNavControls: false,
	renderWorldCopies: true,
	fitBoundsPadding: 16,
	fitBoundsMaxZoom: LOCATIONS_MAP_COUNTRY_LEVEL_MAX_ZOOM,
	lockBoundsAfterFit: false,
	disableDragPan: false,
};

const FULLSCREEN_MAP_GALLERY_PROPS = {
	hideSearchWithinArea: true,
	baseLayerStyle: 'light',
	showNavControls: false,
	renderWorldCopies: true,
	fitBoundsPadding: 48,
	fitBoundsMaxZoom: LOCATIONS_MAP_COUNTRY_LEVEL_MAX_ZOOM,
	lockBoundsAfterFit: false,
	disableDragPan: false,
};

interface LocationsMapViewProps {
	locations: any[];
	popupRenderFunc: (record: any) => any;
	fetchLocationDetails?: (entity: any) => Promise<any>;
	longitudeAccessor: (record: any) => any;
	latitudeAccessor: (record: any) => any;
	detailPathForRecord: (record: any) => string;
	// Fullscreen side list: optional per-record contact groups (primary/facilities/technician),
	// resolved by the caller so role/user-type logic stays out of this generic view.
	contactGroupsAccessor?: (record: any) => LocationContactGroup[];
	// Inline mode: optional expand affordance that opens the fullscreen overlay.
	onExpand?: () => void;
	// Fullscreen overlay mode: when `fullscreen` is set the component renders the
	// full-screen overlay shell (header + toolbar slot + map/sidebar layout) and is
	// gated by `open`. Otherwise it renders the inline 570px panel.
	fullscreen?: boolean;
	open?: boolean;
	onClose?: () => void;
	toolbar?: React.ReactNode;
}

const LocationsMapView: FC<LocationsMapViewProps> = ({
	locations,
	popupRenderFunc,
	fetchLocationDetails,
	longitudeAccessor,
	latitudeAccessor,
	detailPathForRecord,
	contactGroupsAccessor,
	onExpand,
	fullscreen = false,
	open = false,
	onClose,
	toolbar,
}) => {
	const overlayRef = useRef<HTMLDivElement>(null);
	const galleryRef = useRef<any>(null);

	const geolocatedEntities = useMemo(
		() =>
			locations.filter((record) => {
				const longitude = longitudeAccessor(record);
				const latitude = latitudeAccessor(record);
				return hasMapCoordinate(longitude) && hasMapCoordinate(latitude);
			}),
		[locations, longitudeAccessor, latitudeAccessor]
	);

	// Clicking a sidebar row reveals that location's tooltip on the map at the current zoom level.
	// The gallery pans the map into view first when the marker is off-screen; navigation to the
	// detail page stays available via the "View location" link inside the tooltip itself.
	const handleLocationClick = useCallback(
		(record) => () => {
			if (galleryRef.current && typeof galleryRef.current.showEntityPopup === 'function') {
				galleryRef.current.showEntityPopup(record);
			}
		},
		[]
	);

	if (fullscreen) {
		if (!open) {
			return null;
		}

		return (
			<div
				ref={overlayRef}
				className="fixed inset-0 z-[1000] flex flex-col overflow-hidden bg-white"
			>
				{/*
				 * Render all antd popups (filter Popover, Select dropdowns, etc.) inside
				 * this fixed overlay rather than the default document.body portal. At body
				 * level they sit in a different stacking/event context than the overlay, so
				 * clicks on the filter dropdown options don't register; scoping the popup
				 * container to the overlay keeps them interactive.
				 */}
				<ConfigProvider getPopupContainer={() => overlayRef.current || document.body}>
					<div className="flex shrink-0 items-center gap-m px-l py-m">
						<Button type="default" icon={<ArrowLeftOutlined />} onClick={onClose}>
							Back
						</Button>
						<div className="h3regular mb-0 text-character-primary">
							Locations ({locations.length})
						</div>
					</div>
					{toolbar}
					<div className="flex min-h-0 flex-1 flex-col overflow-hidden">
						<div className="flex h-full min-h-0 w-full overflow-hidden bg-white">
							<div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-l">
								{geolocatedEntities.length === 0 ? (
									<div className="flex h-full flex-1 items-center justify-center px-4">
										<Empty description="No locations with map coordinates to show." />
									</div>
								) : (
									<div className="relative h-full min-h-0 w-full overflow-hidden">
										<LocationsMapboxGallery
											key="fullscreen-map"
											ref={galleryRef}
											popupRenderFunc={popupRenderFunc}
											fetchLocationDetails={fetchLocationDetails}
											geolocatedEntities={geolocatedEntities}
											longitudeAccessor={longitudeAccessor}
											latitudeAccessor={latitudeAccessor}
											{...FULLSCREEN_MAP_GALLERY_PROPS}
										/>
									</div>
								)}
							</div>
							<div className="flex w-[390px] shrink-0 flex-col overflow-y-auto overflow-x-hidden border-l border-t-0 border-solid border-grey-5">
								{locations.map((record) => {
									const imageUrl =
										getResolvedImageUrisFromStrings(
											nullSafeGet('images', record),
											nullSafeGet('convertedImages', record)
										)[0] || null;
									const contactGroups = contactGroupsAccessor ? contactGroupsAccessor(record) : [];
									return (
										<div
											key={record.id}
											role="button"
											tabIndex={0}
											className="flex w-full cursor-pointer items-start gap-s border-0 border-b border-solid border-grey-4 bg-white px-m py-l text-left hover:bg-grey-2"
											onClick={handleLocationClick(record)}
											onKeyDown={(event) => {
												// Only act on keys fired on the row itself — let nested controls (the detail
												// link) keep their native Enter/Space activation for keyboard users.
												if (event.target !== event.currentTarget) {
													return;
												}
												if (event.key === 'Enter' || event.key === ' ') {
													event.preventDefault();
													handleLocationClick(record)();
												}
											}}
										>
											<div className="flex min-w-0 flex-1 flex-col gap-2xs">
												<span className="h5medium text-character-primary">
													{record.name}
													{detailPathForRecord ? (
														<Link
															to={detailPathForRecord(record)}
															target="_blank"
															rel="noopener noreferrer"
															className={`ml-1.5 inline-flex align-[-0.125em] ${ROLE_LINK_COLOR_CLASS} hover:opacity-80`}
															aria-label={`Open ${record.name} details`}
															onClick={(event) => event.stopPropagation()}
														>
															<LocationExternalLinkIcon />
														</Link>
													) : null}
												</span>
												<div className="flex min-w-0 items-end gap-xs text-character-secondary">
													<span className="bodyregular truncate">
														{nullSafeGet('buyerFacility.primaryAddress.city', record)}
													</span>
													<span className="footnoteregular shrink-0 uppercase">
														{nullSafeGet('locationType.name', record)}{' '}
														{nullSafeGet('storeId', record)}
													</span>
												</div>
											</div>
											{imageUrl ? (
												<div className="ml-s size-12 shrink-0 overflow-hidden rounded-md bg-grey-3">
													<img
														src={imageUrl}
														alt={record.name}
														className="h-full w-full object-cover"
													/>
												</div>
											) : null}
										</div>
									);
								})}
							</div>
						</div>
					</div>
				</ConfigProvider>
			</div>
		);
	}

	return (
		<div className={INLINE_MAP_CONTAINER_CLASSNAME}>
			{geolocatedEntities.length === 0 ? (
				<div className="flex h-full items-center justify-center">
					<Empty description="No locations with map coordinates to show." />
				</div>
			) : (
				<>
					{onExpand ? (
						<div className="absolute right-2 top-2 z-20">
							<Button
								type="default"
								size="small"
								shape="circle"
								className="flex h-8 w-8 cursor-pointer items-center justify-center border border-solid border-grey-5 bg-white p-0 !text-black/85 shadow-sm hover:!border-grey-6 hover:!text-black/85 focus:!border-grey-6 focus:!text-black/85 active:!border-grey-6 active:!text-black/85 hover:[&_.anticon]:!text-black/85 focus:[&_.anticon]:!text-black/85 active:[&_.anticon]:!text-black/85"
								icon={<FullscreenOutlined style={{ fontSize: 14, color: 'rgba(0,0,0,0.85)' }} />}
								title="Expand map"
								aria-label="Expand map to full screen"
								onClick={onExpand}
							/>
						</div>
					) : null}
					<LocationsMapboxGallery
						key="inline-map"
						popupRenderFunc={popupRenderFunc}
						fetchLocationDetails={fetchLocationDetails}
						geolocatedEntities={geolocatedEntities}
						longitudeAccessor={longitudeAccessor}
						latitudeAccessor={latitudeAccessor}
						{...INLINE_MAP_GALLERY_PROPS}
					/>
				</>
			)}
		</div>
	);
};

export default LocationsMapView;
